import React, { useState, Fragment } from "react";

const InteractiveRotaryGfx = (props) => {
  // Constants

  const title = props.title;
  const width = props.width; // width MUST match the same width as ".rotaryGFXContainer" in "index.css"
  const radius = width / 2;
  const circleRadius = width / 10; // circleRadius MUST be half the width of ".rotaryGFXItem .circle" in "index.css"
  const drawRadius = radius - circleRadius;
  const colors = [
    ["bg-blue-brand-700", "text-blue-brand-700"],
    ["bg-green-brand-700", "text-green-brand-700"],
    ["bg-purple-brand-700", "text-purple-brand-700"],
    ["bg-red-brand-700", "text-red-brand-700"],
    ["bg-teal-brand-700", "text-teal-brand-700"],
    ["bg-blue-brand-700", "text-blue-brand-700"],
    ["bg-green-brand-700", "text-green-brand-700"],
    ["bg-purple-brand-700", "text-purple-brand-700"],
    ["bg-red-brand-700", "text-red-brand-700"],
    ["bg-teal-brand-700", "text-teal-brand-700"],
  ];

  // Methods

  const mapItems = (items) => {
    // Setup item positions on the canvas
    const newItems = items?.map((item, index) => {
      const angle = (360 / items.length) * index - 90;
      const drawRadius = radius - circleRadius;
      const cx = item.isOpen
        ? ""
        : Math.round(
            drawRadius * Math.cos((angle * Math.PI) / 180) +
              radius -
              circleRadius
          );
      const cy = item.isOpen
        ? ""
        : Math.round(
            drawRadius * Math.sin((angle * Math.PI) / 180) +
              radius -
              circleRadius
          );
      const labelFontSize = "text-base";
      const labelPadding = 15;
      const labelWidth = circleRadius * 2 + labelPadding * 2;
      const labelStyle = {};
      let labelTextAlign = "";
      let labelPaddingClass = "";

      // set circleLable styles
      if (cx === drawRadius && cy < drawRadius) {
        // top circle
        labelStyle.top = `-${labelWidth + labelPadding}px`;
        labelStyle.alignItems = "flex-end";
        labelStyle.left = `-${Math.round(labelPadding * 1.5)}px`;
        labelTextAlign = "md:text-center";
        labelPaddingClass = `md:pb-4`;
      } else if (cx === drawRadius && cy > drawRadius) {
        // bottom circle
        labelStyle.bottom = `-${labelWidth + labelPadding}px`;
        labelStyle.alignItems = "flex-start";
        labelStyle.left = `-${Math.round(labelPadding * 1.5)}px`;
        labelTextAlign = "md:text-center";
        labelPaddingClass = `md:pt-4`;
      } else if (cx > drawRadius) {
        // right circles
        labelStyle.left = `${labelWidth - labelPadding * 2}px`;
        labelStyle.alignItems = "center";
        labelTextAlign = "md:text-left";
        labelPaddingClass = `md:pl-4`;
      } else if (cx < drawRadius) {
        // left circles
        labelStyle.left = `-${labelWidth + labelPadding}px`;
        labelStyle.justifyContent = "flex-end";
        labelStyle.alignItems = "center";
        labelTextAlign = "md:text-right";
        labelPaddingClass = `md:pr-4`;
      }

      return {
        ...item,
        angle: angle,
        drawRadius: drawRadius,
        cx: cx,
        cy: cy,
        labelFontSize: labelFontSize,
        labelPadding: labelPadding,
        labelWidth: labelWidth,
        labelStyle: labelStyle,
        labelTextAlign: labelTextAlign,
        labelPaddingClass: labelPaddingClass,
      };
    });
    return newItems;
  };

  const mapRelations = (items, relations) => {
    // Setup relations graphics
    // this creates the arrows and lines in the graphic by creating "path" elements for the SVG
    const newRelations = relations?.map((relation, index) => {
      const item = items[relation[0]];
      const relatedItem = items[relation[1]];

      if (relatedItem && item) {
        // Draw base line
        const relatedItemX = Math.round(
          drawRadius * Math.cos((relatedItem.angle * Math.PI) / 180) + radius
        );
        const relatedItemY = Math.round(
          drawRadius * Math.sin((relatedItem.angle * Math.PI) / 180) + radius
        );
        const startX = item.cx + circleRadius;
        const startY = item.cy + circleRadius;
        const rise = relatedItemY - startY;
        const run = relatedItemX - startX;
        const distance = Math.sqrt(Math.pow(rise, 2) + Math.pow(run, 2));
        const arrowHeadPadding = 10;
        const adjustedDistance = distance - circleRadius - arrowHeadPadding;
        const distanceRatio = adjustedDistance / distance;
        const arrowHeadX = startX + run * distanceRatio;
        const arrowHeadY = startY + rise * distanceRatio;

        // Draw perpandicular line
        const slopeA = rise / run;
        const slopeB = slopeA !== 0 ? -1 / slopeA : 0;
        const yIntercept = arrowHeadY - slopeB * arrowHeadX;
        const rise2 = yIntercept - arrowHeadY;
        const run2 = 0 - arrowHeadX;
        const distance2 = Math.sqrt(Math.pow(rise2, 2) + Math.pow(run2, 2));
        const distanceRatio2 = 5 / distance2;
        const arrowHeadX2 =
          rise && run
            ? arrowHeadX + run2 * distanceRatio2
            : run
            ? arrowHeadX
            : arrowHeadX + 5;
        const arrowHeadY2 =
          rise && run
            ? arrowHeadY + rise2 * distanceRatio2
            : rise
            ? arrowHeadY
            : arrowHeadY + 5;

        // Draw to arrowHead point
        const adjustedDistance3 =
          distance - circleRadius - arrowHeadPadding / 2;
        const distanceRatio3 = adjustedDistance3 / distance;
        const arrowHeadX3 =
          rise && run
            ? startX + run * distanceRatio3
            : rise
            ? arrowHeadX
            : arrowHeadX - 5;
        const arrowHeadY3 =
          rise && run
            ? startY + rise * distanceRatio3
            : run
            ? arrowHeadY
            : relatedItemX > radius
            ? arrowHeadY + 5
            : arrowHeadY - 5;

        // Back to sperpandicular line
        const arrowHeadX4 =
          rise && run
            ? arrowHeadX - run2 * distanceRatio2
            : run
            ? arrowHeadX
            : arrowHeadX - 5;
        const arrowHeadY4 =
          rise && run
            ? arrowHeadY - rise2 * distanceRatio2
            : rise
            ? arrowHeadY
            : arrowHeadY - 5;

        if (relation[2] === "arrow") {
          // Line with arrow

          return (
            <Fragment key={`${item.label} relation  ${index} - ${relation[1]}`}>
              {/* <path
                  className={`guide`}
                  stroke="blue"
                  strokeWidth="1"
                  d={`M${ radius } ${ radius } L${ startX } ${ startY }`}
              /> */}
              {/* <path
                  className={`line line-${ index }`}
                  stroke="black"
                  strokeWidth="1"
                  d={`M${ startX } ${ startY } L${ arrowHeadX } ${ arrowHeadY }`}
              /> */}
              <path
                className={`arrowHead arrowHead-${index}`}
                stroke="#979797"
                strokeWidth="1"
                fill="#979797"
                d={`M${startX} ${startY} L${arrowHeadX} ${arrowHeadY} L${arrowHeadX2} ${arrowHeadY2} L${arrowHeadX3} ${arrowHeadY3} L${arrowHeadX4} ${arrowHeadY4} L${arrowHeadX} ${arrowHeadY}`}
              />
            </Fragment>
          );
        } else {
          // Regular Line

          return (
            <Fragment key={`${item.label} relation  ${index} - ${relation[1]}`}>
              {/* <path
                  className={`guide`}
                  stroke="blue"
                  strokeWidth="1"
                  d={`M${ radius } ${ radius } L${ startX } ${ startY }`}
              /> */}
              <path
                className={`line line-${index}`}
                stroke="#D4D4D4"
                strokeWidth="1"
                d={`M${startX} ${startY} L${relatedItemX} ${relatedItemY}`}
              />
            </Fragment>
          );
        }
      }
    });
    return newRelations;
  };

  const handleItemClick = (i) => {
    const newItems = [];
    items.forEach((item, index) => {
      if (i === index) {
        const newIsItemOpen = !item.isOpen;
        setIsItemOpen(newIsItemOpen);
        item.isOpen = newIsItemOpen;
        newItems.push(item);
      } else {
        item.isOpen = false;
        newItems.push(item);
      }
    });
    setItems(newItems);
  };

  const handleNextClick = (i) => {
    if (i >= items.length - 1) {
      handleItemClick(0);
    } else {
      handleItemClick(i + 1);
    }
  };

  const handlePrevClick = (i) => {
    if (i <= 0) {
      handleItemClick(items.length - 1);
    } else {
      handleItemClick(i - 1);
    }
  };

  // State

  const [isItemOpen, setIsItemOpen] = useState(false);
  const [items, setItems] = useState(mapItems(props.items));
  const [relations, setRelations] = useState(
    mapRelations(items, props.relations)
  );

  return (
    <div
      className={`rotaryGFXContainer mx-auto mb-4 relative md:mt-32 ${
        isItemOpen ? "" : "stopAnimation"
      }`}
      style={{ minHeight: width }}
    >
      <svg
        width={width + "px"}
        style={{ width: "100%", height: "100%" }}
        height={width + "px"}
        viewBox={`0 0 ${width} ${width}`}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        className={`hidden absolute inset-0 ${isItemOpen ? "" : "md:block"}`}
      >
        {!items ? (
          <circle
            cx={radius}
            cy={radius}
            r={drawRadius}
            stroke="red"
            strokeWidth="3"
            fill="transparent"
          />
        ) : null}

        {relations}
        {/** these are the lines that show relationships between items */}
      </svg>

      {/* Title */}
      <div
        className={`h-full justify-center items-center w-full mx-auto hidden absolute justify-center ${isItemOpen || !title || /^\s+$/.test(title) ? "hidden" : "md:flex"}`}
      >
        <div
          className={`text-4xl text-center mb-8 md:mb-0 bg-gray-100 mx-auto w-1/2 p-4 rounded-full`}
        >
          {title}
        </div>
      </div>

      <div className={`rotaryItems`}>
        {items?.map((item, index) => {
          return (
            <div
              className={`rotaryGFXItem rotaryGFXItem-${index} flex flex-row items-center md:absolute hover:${
                colors[index][1]
              } ${
                item.isOpen
                  ? "open absolute m-0 inset-0"
                  : "mb-4 md:m-0 cursor-pointer"
              }`}
              style={{
                top: item.isOpen ? "" : item.cy,
                left: item.isOpen ? "" : item.cx,
              }}
              key={index}
              ref={item.ref}
              onClick={
                item.isOpen
                  ? null
                  : () => {
                      handleItemClick(index, item.ref);
                    }
              }
            >
              {/* Circle */}
              <div
                className={`circle ${colors[index][0]} text-white ${
                  item.isOpen
                    ? "flex flex-col justify-between items-stretch rounded-none md:rounded-full items-start z-10 mx-auto overflow-hidden"
                    : isItemOpen
                    ? "hidden mr-4 md:m-0 rounded-full items-center"
                    : "mr-4 md:m-0 rounded-full items-center"
                }`}
              >
                {/* Item Content */}
                <div
                  className={`h-full ${item.isOpen ? "md:h-auto pt-4" : ""}`}
                >
                  {/* Item Header */}
                  <div className={`md:px-8 ${item.isOpen ? "" : "h-full"}`}>
                    {/* Inner Circle */}
                    <div
                      className={`innerCircle flex justify-center items-center ${
                        item.isOpen
                          ? "bg-white p-8 rounded-full mx-auto mb-4"
                          : "h-full"
                      }`}
                    >
                      {/* Item Title */}
                      <span
                        className={`agrandir circle-title md:text-4xl ${
                          item.isOpen
                            ? `text-4xl ${colors[index][1]}`
                            : "text-3xl text-white"
                        }`}
                      >
                        {item.title}
                      </span>
                    </div>

                    {/* Item Label */}
                    <h2
                      className={`agrandir circle-label truncate text-center md:mb-4 text-white mx-auto ${
                        item.labelFontSize
                      } md:w-full ${item.isOpen ? "px-4" : "hidden"}`}
                    >
                      {item.label}
                    </h2>

                    <hr
                      className={`w-full border-t-2 border-white my-4 md:hidden ${
                        item.isOpen ? "" : "hidden"
                      }`}
                    />
                  </div>

                  {/* Item Body */}
                  <div
                    className={`item-body items-center ${
                      item.isOpen ? "flex flex-row" : "hidden"
                    }`}
                  >
                    {/* Previous Button */}
                    <div
                      className={`next flex justify-center items-center w-1/5 text-center h-full ${
                        item.isOpen ? "" : "hidden"
                      }`}
                    >
                      {/* <span className={`text-4xl`}
                                                        onClick={ item.isOpen ? () => { handlePrevClick( index, item.ref ) } : null }
                                                    >&lt;</span> */}
                      <i
                        className="fas fa-chevron-left text-5xl cursor-pointer"
                        onClick={
                          item.isOpen
                            ? () => {
                                handlePrevClick(index, item.ref);
                              }
                            : null
                        }
                      ></i>
                    </div>

                    {/* Item Details */}
                    <div
                      className={`item-details w-full text-white text-base mx-auto overflow-y-scroll overflow-x-hidden p-2 ${
                        item.isOpen ? "" : "hidden"
                      }`}
                      style={{ height: width * 0.46 }}
                    >
                      {<p className={`font-bold text-lg mb-4`}>{item.exerp}</p>}
                      <ul className={`ml-4 list-disc`}>
                        {item.body.split("\n").map((line, index) => {
                          return (
                            <li key={index} className={`mb-4`}>
                              {line}
                            </li>
                          );
                        })}
                      </ul>
                    </div>

                    {/* Next Button */}
                    <div
                      className={`next flex justify-center items-center w-1/5 text-center h-full ${
                        item.isOpen ? "" : "hidden"
                      }`}
                    >
                      {/* <span className={`text-4xl`}
                                                        onClick={ item.isOpen ? () => { handleNextClick( index, item.ref ) } : null }
                                                    >&gt;</span> */}
                      <i
                        className="fas fa-chevron-right text-5xl cursor-pointer"
                        onClick={
                          item.isOpen
                            ? () => {
                                handleNextClick(index, item.ref);
                              }
                            : null
                        }
                      ></i>
                    </div>
                  </div>
                </div>

                {/* Back to Diagram */}
                <div
                  className={`back-to-diagram agrandir cursor-pointer flex flex-1 justify-center items-center bg-white md:bg-transparent md:text-white text-base ${
                    colors[index][1]
                  } border border-blue-700 md:border-none pt-4 pb-4 md:pb-0 md:mb-4 ${
                    item.isOpen ? "md:w-1/3 md:mx-auto" : "hidden"
                  }`}
                  onClick={() => {
                    handleItemClick(index, item.ref);
                  }}
                >{`Back to Diagram`}</div>
              </div>

              {/* Circle Label */}
              <div
                className={`circleLabel md:absolute ${item.labelPaddingClass} ${
                  item.labelTextAlign
                } ${isItemOpen ? "hidden" : "md:flex"}`}
                style={item.labelStyle}
              >
                {item.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default InteractiveRotaryGfx;
